import { Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NwNumber } from '../../../../../shared/models/nw-number/nw-number';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { NgClass } from '@angular/common';
import { NwNumberAccordionComponent } from '../../../lists/nw-number/nw-number-accordion/nw-number-accordion.component';
import { GroupedNwNumber } from '../../../../../shared/models/nw-number/grouped-nw-number';
import { NwNumberApiService } from '../../../../services/api/nw-number-api.service';
import { CommonUtils, errorToString } from '../../../../common/common-utils';
import { firstValueFrom } from 'rxjs';
import { SidebarConfiguration } from '../../../../../shared/internal/sidebar/sidebar-configuration';
import { SIDEBAR_AREA } from '../../../../../shared/constants/sidebar-area';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { SidebarComponent } from '../../../common/sidebar/sidebar.component';
import { EditComponentType } from '../../../../../shared/enums/edit-component-type';
import { Textarea } from '../../../../../shared/models/internal/details/textarea';
import { ApprovalStatus } from '../../../../../shared/models/internal/details/approval-status';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { DecisionLevel } from '../../../../../shared/enums/decision-level';
import { Decision } from '../../../../../shared/models/internal/decision';
import { LanguageTranslationState } from '../../../../../shared/models/dtc/language-translation-state';
import { AggreatedLanguageTranslationState } from '../../../../../shared/models/dtc/aggreated-language-translation-state';
import { CreateNwNumber } from '../../../../../shared/models/nw-number/create-nw-number';
import { Project } from '../../../../../shared/models/project';
import { ProjectApiService } from '../../../../services/api/project-api.service';
import { LabelValue } from '../../../../../shared/models/internal/details/label-value';
import { SingleLineDetail } from '../../../../../shared/models/internal/details/single-line-detail';
import { ImportType } from '../../../lists/nw-number/nw-number-accordion/import-type';
import { ErrorCodeApiService } from '../../../../services/api/error-code-api.service';
import { AlarmMaskApiService } from '../../../../services/api/alarm-mask-api.service';
import { ExportType } from '../../../lists/nw-number/nw-number-accordion/export-type';
import { TrReviewStatistics } from '../../../../../shared/models/internal/details/tr-review-statistics';

@Component({
  selector: 'app-nw-number-overview',
  imports: [NgClass, NwNumberAccordionComponent, SidebarComponent, DecisionModalComponent],
  templateUrl: './nw-number-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './nw-number-overview.component.scss',
})
export class NwNumberOverview extends CrudOverviewBaseComponent<NwNumber> implements OnInit {
  @ViewChild('createNwNumberModal')
  public createNwNumberModal!: DecisionModalComponent;
  @ViewChild('proceedImportModal')
  public proceedImportModal!: DecisionModalComponent;

  public groupedNwNumbers: GroupedNwNumber[] = [];
  public nwNumber?: NwNumber;
  public project?: Project;
  public sidebarConfiguration: SidebarConfiguration = new SidebarConfiguration();
  public nwNumberDetailConfiguration?: DetailsViewConfiguration;
  public newNwNumberToCreate?: CreateNwNumber;
  public readonly decisionLevel = DecisionLevel.Warning;
  public aggregatedLanguageTranslationStatesDocumentation: LanguageTranslationState[] = [];
  public aggregatedLanguageTranslationStatesTerminal: LanguageTranslationState[] = [];
  public currentEditComponentType?: EditComponentType;

  public modalProceedImportHeader = '';
  public modalProceedImportDetails = [''];

  private readonly nwNumberApiService = inject(NwNumberApiService);
  private readonly projectApiService = inject(ProjectApiService);
  private readonly errorCodeApi = inject(ErrorCodeApiService);
  private readonly alarmMaskApi = inject(AlarmMaskApiService);

  private importCallback: () => Promise<void>;
  private importNwNumber: NwNumber | undefined;
  private importProject: Project | undefined;
  private importFiles: File[] | undefined;

  constructor() {
    super(STORAGE_KEYS.UNSAVED_NW_NUMBER);
    this.importCallback = async () => {
      throw new Error('Callback not set');
    };
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.fetchGroupedNwNumbers();
  }

  public getItemsTotalCount(): number {
    return this.groupedNwNumbers.reduce((total, group) => total + (group.nwNumbers ?? []).length, 0);
  }

  public async onProjectClicked(projectId: string): Promise<void> {
    // TODO: protect changed project data
    if (
      this.areThereUnsavedChanges<NwNumber>(
        this.getFriendlyItemName(this.selectedItem),
        () => this.allowUnsavedChanges(),
        () => this.preventUnsavedChanges(this.selectedItem!)
      ) === false
    ) {
      this.project = await this.fetchProject(projectId);
      this.configureSidebarForProject();
      this.configureDetailsViewForProject(this.project);
      this.sideBarTitle = 'Project ' + (this.project?.name ?? '');
      this.sidebarOpen = true;
      this.currentEditComponentType = EditComponentType.Project;
      this.fetchAggregatedTranslationStatesForProject(projectId);
    }
  }

  public async onNwNumberClicked(nwNumberId: string): Promise<void> {
    // TODO: protect changed project data
    if (
      this.areThereUnsavedChanges<NwNumber>(
        this.getFriendlyItemName(this.selectedItem),
        () => this.allowUnsavedChanges(),
        () => this.preventUnsavedChanges(this.selectedItem!)
      ) === false
    ) {
      this.nwNumber = await this.fetchNwNumber(nwNumberId);
      this.selectedItem = this.nwNumber;
      this.configureSidebarForNwNumber();
      this.configureDetailsViewForNwNumber(this.nwNumber);
      this.sideBarTitle = 'NW Number ' + (this.nwNumber?.number ?? '');
      this.sidebarOpen = true;
      this.fetchAggregatedTranslationStates(nwNumberId);
      this.currentEditComponentType = EditComponentType.NwNumber;
    }
  }

  public onCreateNwNumber(data: CreateNwNumber): void {
    this.newNwNumberToCreate = data;
    this.createNwNumberModal.openModal();
  }

  public handleCreateNwNumberDecision(decision: Decision): void {
    if (
      decision.result &&
      this.newNwNumberToCreate &&
      this.newNwNumberToCreate.projectId &&
      this.newNwNumberToCreate.number
    ) {
      this.nwNumberApiService.createNwNumber(this.newNwNumberToCreate).subscribe({
        next: () => {
          this.fetchGroupedNwNumbers();
        },
        error: (error: Error) => {
          console.error(errorToString(error));
        },
      });
    }
  }

  public async handleProceedImportDecision(decision: Decision): Promise<void> {
    if (decision.result) {
      await this.importCallback();
    }
    this.toastr.success('Import started successfully.');
  }

  public onUpdatedProject(project: Project): void {
    this.fetchGroupedNwNumbers();
    this.project = project;
    this.selectedTableElement.setSelectedElementId(project.id ?? '', -1);
    this.configureDetailsViewForProject(this.project!);
    this.clearUnsavedData();
  }

  public async onNwNumberImport(upload: { nwNumber: NwNumber; files: File[]; importType: ImportType }): Promise<void> {
    this.importFiles = upload.files;
    this.importNwNumber = upload.nwNumber;

    switch (upload.importType) {
      case ImportType.ImportErrorCodes:
        this.modalProceedImportHeader = 'Clear existing data';
        this.modalProceedImportDetails = [
          'This action deletes error codes exclusively linked to this NW number. Error codes shared with other NW numbers will be preserved and only unlinked from this specific NW number.',
          'Do you want to proceed?',
        ];
        this.importCallback = async () => {
          await this.importErrorCodesAsync();
        };
        break;
      case ImportType.ImportTrDocumentation:
        this.modalProceedImportHeader = 'Import TR Documentation';
        this.modalProceedImportDetails = [
          'This action imports revised documentation for this NW number.',
          'Do you want to proceed?',
        ];
        // TODO: 806: use different endpoints depending on file type
        this.importCallback = async () => {
          await this.importErrorCodesTRDocumentation();
        };
        break;
      default:
        throw new Error('Unxepected ImportType detected: ' + upload.importType);
    }
    this.proceedImportModal.openModal();
    this.handleNwNumberUpdated(upload.nwNumber);
  }

  public async onProjectImport(upload: { project: Project; files: File[]; importType: ImportType }): Promise<void> {
    this.importFiles = upload.files;
    this.importProject = upload.project;
    let displayDialog = false;

    switch (upload.importType) {
      case ImportType.ImportAlarmMask: {
        const importStatus = await firstValueFrom(
          this.alarmMaskApi.getImportStatus({ xmlFile: upload.files![0], projectId: this.importProject.id })
        );
        displayDialog = importStatus.projectHasExistingAlarmMasks ?? false;
        this.modalProceedImportHeader = 'Alarm masks for this project already exist';
        this.modalProceedImportDetails = [
          'This action deletes existing alarm masks before import.',
          'Do you want to proceed?',
        ];
        this.importCallback = async () => {
          await this.importAlarmMaskAsync();
        };
        break;
      }
      case ImportType.ImportAlarmMaskTranslation: {
        const importTranslationStatus = await firstValueFrom(
          this.alarmMaskApi.getImportTranslationStatus({ xmlFiles: this.importFiles, projectId: this.importProject.id })
        );
        displayDialog = importTranslationStatus.existingLanguages.length > 0;
        this.modalProceedImportHeader = 'Translations for this project already exist';
        this.modalProceedImportDetails = [
          'This action deletes existing translations before import.',
          'Do you want to proceed?',
        ];
        this.importCallback = async () => {
          await this.importAlarmMaskTranslationsAsync();
        };
        break;
      }
      default:
        throw new Error('Unxepected ImportType detected: ' + upload.importType);
    }

    if (displayDialog) {
      this.proceedImportModal.openModal();
    } else {
      await this.importCallback();
      this.toastr.success('Import started successfully.');
    }
    this.handleProjectUpdate(upload.project);
  }

  public sendServiceBusEvents(nwNumber?: string): void {
    this.nwNumberApiService.publishSyncEvents(nwNumber ?? '').subscribe({
      next: () => {
        this.toastr.success('Published data synchronisation events (Error Codes)');
      },
      error: () => {
        console.error('Error publishing data synchronisation events');
      },
    });
  }

  public onNwNumberExport(download: { nwNumber: NwNumber; exportType: ExportType }): void {
    if (download.exportType != ExportType.ExportTrDocumentation) {
      throw new Error('Unhandled ExportType detected');
    }
    this.errorCodeApi.exportTrDocumentationFields(download.nwNumber.id!).subscribe({
      next: (response) => {
        CommonUtils.downloadFile(response, `tr-ec-documentation-fields-${download.nwNumber.number!}.xml`);
      },
      error: (error) => {
        console.error('Error exporting TR documentation fields:', errorToString(error));
      },
    });
  }

  protected override handleNewSelection(id: string): void {
    console.debug(id);
    // Not required since we are handling the selection in handleNwNumberClicked
  }

  protected override getFriendlyItemName(item?: NwNumber | undefined): string {
    console.debug(item);
    return item?.number ?? '';
  }

  protected handleNwNumberUpdated(updatedNwNumber: NwNumber): void {
    this.fetchGroupedNwNumbers();
    this.nwNumber = updatedNwNumber;
    this.selectedItem = updatedNwNumber;
    this.selectedTableElement.setSelectedElementId(updatedNwNumber.id ?? '', -1);
    this.configureDetailsViewForNwNumber(this.nwNumber!);
    this.clearUnsavedData();
  }

  protected handleProjectUpdate(updatedProject: Project): void {
    this.fetchGroupedNwNumbers();
    this.project = updatedProject;
    this.selectedItem = updatedProject;
    this.selectedTableElement.setSelectedElementId(this.project.id ?? '', -1);
    this.configureDetailsViewForProject(this.project!);
    this.clearUnsavedData();
  }

  protected override configureDetailsView(item: NwNumber): void {
    this.configureDetailsViewForNwNumber(item);
  }

  protected configureDetailsViewForNwNumber(item: NwNumber): void {
    const textAreas: Textarea[] = [
      { sequence: 1, label: 'Product Line', value: item.productLine ?? 'N/A', isTranslatable: false },
      { sequence: 2, label: 'Short Description', value: item.shortDescription ?? 'N/A', isTranslatable: false },
      {
        sequence: 4,
        label: 'Error Codes with Alarm Mask',
        value: (item.errorCodesWithExistingAlarmMasksCount ?? 0) + '/' + (item.totalErrorCodesCount ?? 0),
        isTranslatable: false,
      },
    ];

    const approvalStats = item.nwNumberApprovalStats
      ? [
          {
            sequence: 6,
            approvalStatus: item.nwNumberApprovalStats,
          } as ApprovalStatus,
        ]
      : undefined;

    const trReviewStatistics = item.trReviewStats
      ? [
          {
            sequence: 7,
            trReviewStatistics: item.trReviewStats,
          } as TrReviewStatistics,
        ]
      : undefined;
    const separators = [3, 5];

    this.nwNumberDetailConfiguration = {
      title: item.displayId,
      textAreas: textAreas,
      displayLinkedDtcs: false,
      internalNotes: item.internalNotes,
      separators: separators,
      approvalStats: approvalStats,
      trReviewStats: trReviewStatistics,
    };
  }

  protected configureDetailsViewForProject(item: Project): void {
    const textAreas: Textarea[] = [
      { sequence: 1, label: 'Product Line', value: item.productLine ?? 'N/A', isTranslatable: false },
      { sequence: 2, label: 'Short Description', value: item.shortDescription ?? 'N/A', isTranslatable: false },
      {
        sequence: 4,
        label: 'Alarm Masks',
        value: (item.totalAlarmMaskCount ?? 0).toString(),
        isTranslatable: false,
      },
    ];

    const labelValue: LabelValue = {
      label: 'Last import',
      value:
        (item.lastImportedBy ?? 'N/A') + ' - ' + (item.lastImport ? new Date(item.lastImport).toLocaleString() : 'N/A'),
    };
    const singleLine: SingleLineDetail = {
      sequence: 5,
      attributes: [labelValue],
    };

    const separators = [3, 6];

    this.nwNumberDetailConfiguration = {
      title: item.name,
      textAreas: textAreas,
      displayLinkedDtcs: false,
      internalNotes: item.internalNotes,
      separators: separators,
      approvalStats: [],
      singleLineDetails: [singleLine],
    };
  }

  private fetchGroupedNwNumbers(): void {
    this.nwNumberApiService.getAllGroupedNwNumbers().subscribe({
      next: (groupedNwNumbers: GroupedNwNumber[]) => {
        this.groupedNwNumbers = groupedNwNumbers;
      },
      error: (error) => {
        console.error('Error fetching NW Numbers:', errorToString(error));
      },
    });
  }

  private async importErrorCodesAsync(): Promise<void> {
    await firstValueFrom(
      this.errorCodeApi.importErrorCodes({
        excelFile: this.importFiles![0],
        plainNwNumber: this.importNwNumber?.number,
      })
    );
  }

  private async importErrorCodesTRDocumentation(): Promise<void> {
    const file = this.importFiles![0];
    const isXml = file.name.toLowerCase().endsWith('xml');
    await firstValueFrom(
      this.errorCodeApi.importTrDocData(
        {
          file: file,
          plainNwNumber: this.importNwNumber?.number,
        },
        isXml
      )
    );
  }

  private async importAlarmMaskAsync(): Promise<void> {
    await firstValueFrom(
      this.alarmMaskApi.importAlarmMask({ xmlFile: this.importFiles![0], projectId: this.importProject!.id })
    );
  }

  private async importAlarmMaskTranslationsAsync(): Promise<void> {
    await firstValueFrom(
      this.alarmMaskApi.importAlarmMaskTranslations({ xmlFiles: this.importFiles, projectId: this.importProject!.id })
    );
  }

  private async fetchNwNumber(id: string): Promise<NwNumber> {
    const nwNumber = await firstValueFrom(this.nwNumberApiService.getByIdAsync(id, true));
    return nwNumber;
  }

  private async fetchProject(id: string): Promise<Project> {
    const project = await firstValueFrom(this.projectApiService.getByIdAsync(id, true));
    return project;
  }

  private fetchAggregatedTranslationStates(nwNumberId?: string): void {
    if (!nwNumberId) {
      this.aggregatedLanguageTranslationStatesDocumentation = [];
      this.aggregatedLanguageTranslationStatesTerminal = [];
      return;
    }

    this.nwNumberApiService.getAggregatedTranslationStates(nwNumberId).subscribe({
      next: (data: AggreatedLanguageTranslationState) => {
        this.aggregatedLanguageTranslationStatesTerminal = data.translationStatesTerminal;
        this.aggregatedLanguageTranslationStatesDocumentation = data.translationStatesDocumentation;
      },
      error: (error) => {
        console.error('Error fetching Aggregated Translation States:', errorToString(error));
      },
    });
  }

  private fetchAggregatedTranslationStatesForProject(projectId?: string): void {
    if (!projectId) {
      this.aggregatedLanguageTranslationStatesDocumentation = [];
      this.aggregatedLanguageTranslationStatesTerminal = [];
      return;
    }
    this.projectApiService.getAggregatedTranslationStates(projectId).subscribe({
      next: (data: AggreatedLanguageTranslationState) => {
        this.aggregatedLanguageTranslationStatesTerminal = data.translationStatesTerminal;
        this.aggregatedLanguageTranslationStatesDocumentation = data.translationStatesDocumentation;
      },
      error: (error) => {
        console.error('Error fetching Aggregated Translation States for Project:', errorToString(error));
      },
    });
  }

  private configureSidebarForNwNumber(): void {
    this.sidebarConfiguration.translationsSettingsAreaEnabled = false;
    this.sidebarConfiguration.auditLogAreaEnabled = true;
    this.sidebarConfiguration.editAreaEnabled = true;
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = true;
    this.sidebarConfiguration.summaryAreaEnabled = true;
    this.sidebarConfiguration.initArea = SIDEBAR_AREA.summary;
  }

  private configureSidebarForProject(): void {
    this.sidebarConfiguration.translationsSettingsAreaEnabled = false;
    this.sidebarConfiguration.auditLogAreaEnabled = true;
    this.sidebarConfiguration.editAreaEnabled = true;
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = true;
    this.sidebarConfiguration.summaryAreaEnabled = true;
    this.sidebarConfiguration.initArea = SIDEBAR_AREA.summary;
  }
}
